import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { getPublicSurface } from "@/lib/public-marketing";

export type ProductAnalyticsDeviceClass = "phone" | "tablet" | "desktop";

export const getProductDeviceClass = (): ProductAnalyticsDeviceClass => {
  if (typeof window === "undefined") return "desktop";

  if (window.innerWidth < 768) return "phone";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
};

export const captureProductAnalytics = (event: string, properties?: Record<string, unknown>) => {
  try {
    posthog.capture(event, properties);
  } catch {
    // best effort only
  }
};

export const captureStudentActivationAnalytics = (
  event: string,
  properties?: Record<string, unknown> & {
    surface?: string | null;
    authenticated?: boolean;
    activation_stage?: string;
  },
) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : undefined;
  captureProductAnalytics(event, {
    device_class: getProductDeviceClass(),
    pathname,
    surface: properties?.surface ?? (pathname ? getPublicSurface(pathname) : null),
    authenticated: properties?.authenticated ?? false,
    activation_stage: properties?.activation_stage ?? null,
    ...properties,
  });
};

export const captureStudentResourceAnalytics = (
  event: string,
  properties?: Record<string, unknown> & {
    surface?: string | null;
    authenticated?: boolean;
  },
) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : undefined;
  captureProductAnalytics(event, {
    device_class: getProductDeviceClass(),
    pathname,
    surface: properties?.surface ?? pathname ?? null,
    authenticated: properties?.authenticated ?? true,
    ...properties,
  });
};

export const capturePublicAnalytics = (
  event: string,
  properties?: Record<string, unknown> & {
    surface?: string | null;
    authenticated?: boolean;
    source_page?: string;
  },
) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : undefined;
  captureProductAnalytics(event, {
    device_class: getProductDeviceClass(),
    authenticated: properties?.authenticated ?? false,
    source_page: properties?.source_page ?? pathname,
    surface: properties?.surface ?? (pathname ? getPublicSurface(pathname) : null),
    ...properties,
  });
};

export const useProductSectionView = (
  event: string,
  properties?: Record<string, unknown>,
  enabled = true,
) => {
  const ref = useRef<HTMLElement | null>(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const node = ref.current;
    if (!node || hasTracked.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (!visible || hasTracked.current) return;

        captureProductAnalytics(event, properties);
        hasTracked.current = true;
        observer.disconnect();
      },
      { threshold: 0.35 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [enabled, event, properties]);

  return ref;
};
